'use client'

import { useEffect, useRef } from 'react'
import { useSession } from 'next-auth/react'

declare global {
  interface Window {
    __ssoLoginPending?: boolean
  }
}

/**
 * 全局 SSO Token 自动登录组件
 * 检测 URL 中的 sso_token 参数，自动调用 /api/auth/sso-login 完成登录
 * 放在 Providers 中，所有页面均可触发
 */
export function SsoAutoLogin() {
  const { status, update } = useSession()
  const attemptedRef = useRef(false)

  // 同步检测 sso_token，立即设置全局标记，阻止其他组件跳转登录页
  if (typeof window !== 'undefined' && !attemptedRef.current) {
    const params = new URLSearchParams(window.location.search)
    if (params.get('sso_token')) {
      window.__ssoLoginPending = true
    }
  }

  useEffect(() => {
    if (attemptedRef.current) return
    if (typeof window === 'undefined') return

    const params = new URLSearchParams(window.location.search)
    const ssoToken = params.get('sso_token')
    if (!ssoToken) return

    // sso_token is authoritative. If the browser still has an old NextAuth
    // session, waiting for `unauthenticated` leaves the page running as the
    // wrong user and can race server component rendering after SSO redirects.
    if (status === 'loading') return

    attemptedRef.current = true

    void (async () => {
      try {
        const res = await fetch('/api/auth/sso-login', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sso_token: ssoToken }),
        })

        if (res.ok) {
          // 刷新客户端 session
          try { await update() } catch { /* ignore */ }

          // 清除标记
          window.__ssoLoginPending = false

          // 移除 URL 中的 sso_token 参数
          const cleanParams = new URLSearchParams(window.location.search)
          cleanParams.delete('sso_token')
          const cleanUrl = cleanParams.toString()
            ? `${window.location.pathname}?${cleanParams.toString()}`
            : window.location.pathname
          window.location.replace(cleanUrl)
          return
        }
      } catch {
        // 登录失败，静默处理
      }

      // 登录失败，清除标记
      window.__ssoLoginPending = false
    })()
  }, [status, update])

  return null
}
